import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { FieldValues } from '../../types';
import { buildFilesArray } from './utils';
import File from './File';
import styles from './Attachments.module.scss';

interface AttachmentsProps {
  onSetFiles: (payload: FieldValues['files']) => void;
  files: FieldValues['files'];
  filesLimit: number;
  error: boolean;
  messages?: Record<number, string | undefined>;
}

const Attachments = ({ onSetFiles, files, filesLimit, error, messages }: AttachmentsProps): React.ReactElement => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const limitValidation = files.length <= filesLimit;

  const handleDrag = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (limitValidation) {
        if (e.type === 'dragenter' || e.type === 'dragover') {
          setDragActive(true);
        } else if (e.type === 'dragleave') {
          setDragActive(false);
        }
      }
    },
    [limitValidation]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (limitValidation) {
        setDragActive(false);
        onSetFiles(buildFilesArray(files, e.dataTransfer.files));
      }
    },
    [onSetFiles, files, limitValidation]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();

      if (limitValidation) {
        onSetFiles(buildFilesArray(files, e.currentTarget.files));
      }

      e.currentTarget.value = '';
    },
    [onSetFiles, files, limitValidation]
  );

  const handleClick = useCallback(() => {
    if (limitValidation) {
      inputRef.current?.click();
    }
  }, [limitValidation]);

  const handleRemove = useCallback(
    (file: FieldValues['files'][number]) => {
      onSetFiles(files.filter((item) => item !== file));
    },
    [onSetFiles, files]
  );

  return (
    <div className={styles.attachments}>
      <div
        className={classNames(styles.attachmentsDnd, {
          [styles.attachmentsDnd_state_error]: error && files.length > 0,
        })}
        onDragEnter={handleDrag}
      >
        {error && files.length > 0 && files.length < filesLimit ? (
          <>
            <div>
              <b>1 or more files have errors:</b>
            </div>
            <div>Remove file to continue</div>
          </>
        ) : null}
        {files.length >= filesLimit ? (
          <>
            <div>
              <b>Max file limit reached:</b>
            </div>
            <div>Remove file to enable</div>
          </>
        ) : null}
        {(!error && files.length < filesLimit) || files.length === 0 ? (
          <>
            <div>Drag and drop or</div>
            <div>
              <span className={styles.attachmentsLink} onClick={handleClick}>
                browse your files
              </span>
            </div>
          </>
        ) : null}
        <input ref={inputRef} type="file" onChange={handleChange} max={10} multiple />
        {dragActive ? (
          <div
            className={styles.attachmentsDrop}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          />
        ) : null}
      </div>
      <div className={styles.attachmentsFiles}>
        {files?.map((file, index) => (
          <File key={file.name} file={file} onRemove={handleRemove} error={messages?.[index]} />
        ))}
      </div>
    </div>
  );
};

export default Attachments;
